import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { useEffect, useMemo } from 'react'
import { routes } from 'src/app/router/routes'

import {
  AddToCartMutation,
  AddToCartMutationVariables,
  PersonalInformationType,
  RemoveFromCartMutation,
  RemoveFromCartMutationVariables,
  StoreCardDetailFragmentDoc,
  UserAddressFragmentDoc,
  UserCartFragment,
  UserCartQuery,
  UserCartQueryVariables,
  ValidateCartQuery,
  ValidateCartQueryVariables,
} from '@/clients/utoniq-core/schema'
import { useInitialLoading } from '@/components/lib/initialLoading'
import { config } from '@/config'
import { useCurrentUser, useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { groupItemsByCollection } from '@/core/cart'
import { App } from '@/core/native'
import {
  getUserInfoRegisterRedirect,
  removeStoreProceed,
  setUserInfoRegisterRedirect,
} from '@/core/native/storage'
import { convertUserCartItemsToSku } from '@/core/sku'
import { useAnalytics } from '@/lib/analytics'

import { Action, AddToCartError, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('storeCartDetail')
  const { router, routerPages, generatePath } = useRouter<{ cartId: string }>()
  const cartId = router.query.cartId
  const { analytics } = useAnalytics()
  const { showError } = useMessage()
  const { user } = useCurrentUser()
  const { data, loading } = useQuery<UserCartQuery, UserCartQueryVariables>(
    QUERY,
    {
      variables: { id: cartId },
      onCompleted({ userCart }) {
        if (!userCart) {
          router.goBack(routerPages.StoreCartList)
        }
      },
    }
  )
  const [validateCart, { loading: validatingCart, error }] = useLazyQuery<
    ValidateCartQuery,
    ValidateCartQueryVariables
  >(VALIDATE_CART, {
    // バリデーションチェック後、カートのアイテムとユーザーの登録状態から適切な遷移先に遷移する
    async onCompleted() {
      const userInfoRegisterRedirect = await getUserInfoRegisterRedirect()

      const redirectPath = userInfoRegisterRedirect.phoneNumber
        ? routes.StoreUserInfo.path
        : userInfoRegisterRedirect.address
        ? routerPages.StoreDeliveryInfo
        : userInfoRegisterRedirect.direction

      router.push(redirectPath)
    },
    onError(e) {
      showError(
        e.graphQLErrors[0].extensions?.userMessage ??
          t('verifyCardErrorMessage')
      )
      analytics?.track('error_cart_checkout', {
        applicationKey: config.applicationKey,
        artistId: data?.userCart?.artist.id,
        error: e.name,
        errorCode: e.graphQLErrors[0].extensions?.errorCode ?? '',
      })
    },
  })
  const [addToCart, { loading: addingToCart, error: addToCartError }] =
    useMutation<AddToCartMutation, AddToCartMutationVariables>(ADD_TO_CART)
  const [removeFromCart, { loading: removingFromCart }] = useMutation<
    RemoveFromCartMutation,
    RemoveFromCartMutationVariables
  >(REMOVE_FROM_CART)

  // SKUのあるアイテムを分割
  const cartItems = convertUserCartItemsToSku(
    data?.userCart?.items ?? null
  ) as UserCartFragment['items']

  const personalInformationRequirements = useMemo<string[]>(() => {
    if (!cartItems) return []

    const result = cartItems.reduce((acc: string[], item) => {
      const requirements = item.item.personalInformationRequirements

      return Array.isArray(requirements) ? acc.concat(requirements) : acc
    }, [])

    return [...new Set(result)]
  }, [cartItems])

  /** 遷移先をコンテキストにセットする */
  useEffect(() => {
    // 電番登録が必要か
    const requirePhoneNumber =
      !(data?.user?.phoneNumber && data?.user?.isPhoneNumberVerified) &&
      personalInformationRequirements.includes(
        PersonalInformationType.PhoneNumber
      )
    // 住所登録が必要か
    const requireAddRess =
      !data?.user?.address?.fullAddress &&
      personalInformationRequirements.includes(PersonalInformationType.Address)

    setUserInfoRegisterRedirect({
      direction: generatePath(routerPages.StoreItemPurchaseProceed, {
        cartId,
      }),
      phoneNumber: requirePhoneNumber,
      address: requireAddRess,
    })
  }, [data?.user, personalInformationRequirements])

  useEffect(() => {
    removeStoreProceed()
  }, [])

  return {
    state: {
      loading: useInitialLoading(data, loading),
      isAnonymous: !!user?.isAnonymous,
      cart: data?.userCart ?? null,
      isSubmitting: validatingCart,
      cartItems,
      changingQualityCart: removingFromCart || addingToCart,
      cartItemsByCollection: groupItemsByCollection(cartItems),
      personalInformationRequirements,
      errors: error?.graphQLErrors?.[0]?.extensions?.exception?.errors,
      addToCartErrors: addToCartError?.graphQLErrors.map(
        e => (e.extensions as { exception: AddToCartError }).exception
      ),
    },
    actions: {
      async onValidateCart() {
        if (!data?.userCart) return

        await validateCart({
          variables: {
            inputs: {
              cartId: data.userCart.id,
              totalPrice: data.userCart.totalPrice,
              isKonbini: false,
            },
          },
        })
      },
      onAddToCart: async (itemId: string, skuId?: string) => {
        if (!data?.userCart) return

        return addToCart({
          variables: {
            inputs: {
              itemId,
              skuId,
              count: 1,
            },
          },
          optimisticResponse: {
            addToCart: {
              ...data.userCart,
              totalCount: data.userCart.totalCount + 1,
              items: data.userCart.items.map(item => {
                if (!skuId) {
                  return item.item.id === itemId
                    ? {
                        ...item,
                        count: item.count + 1,
                      }
                    : item
                }

                return {
                  ...item,
                  skus: item.skus.map(sku => {
                    if (sku.skuId !== skuId) return sku
                    return {
                      ...sku,
                      count: sku.count + 1,
                    }
                  }),
                }
              }),
            },
          },
        })
          .then(({ data: _data }) => {
            const targetItem = data?.userCart?.items.find(
              item => item.item.id === itemId
            )

            analytics?.track('add_to_cart', {
              item_list_id: `artist_store_list_${data?.userCart?.artist.id}`,
              item_list_name: 'Artist Store Items',
              isAnonymous: !!user?.isAnonymous,
              items: [
                {
                  item_id: itemId,
                  item_name: targetItem?.item.title,
                  item_category: 'store_item',
                  item_category2: targetItem?.item?.type,
                  item_category3: App.getPlatform(),
                  item_category4: skuId,
                  item_category5: data?.userCart?.artist.id,
                  price: targetItem?.item?.price ?? 0,
                  quantity: 1,
                },
              ],
            })
          })
          .catch(e => {
            analytics?.track('error_cart_checkout', {
              applicationKey: config.applicationKey,
              artistId: data?.userCart?.artist.id,
              error: e.name,
              errorCode: e.graphQLErrors[0].extensions?.errorCode ?? '',
            })
            showError(e)
          })
      },
      onRemoveFromCart: async (
        itemId: string,
        count: number,
        skuId: string | undefined,
        removeAll?: boolean
      ) => {
        if (!data?.userCart) return

        return removeFromCart({
          variables: {
            inputs: {
              itemId,
              cartId,
              skuId,
              count,
            },
          },
          optimisticResponse: {
            removeFromCart: {
              ...data.userCart,
              totalCount: data.userCart.totalCount - count,
              items:
                data.userCart?.totalCount === count
                  ? []
                  : data.userCart.items
                      .filter(item => !(item.item.id === itemId && removeAll))
                      .flatMap(item => {
                        if (item.item.id !== itemId) {
                          return item
                        }
                        if (!skuId) {
                          return {
                            ...item,
                            count: item.count - count,
                          }
                        }

                        return {
                          ...item,
                          skus: item.skus.flatMap(sku => {
                            if (sku.skuId !== skuId) return sku
                            return {
                              ...sku,
                              count: sku.count - count,
                            }
                          }),
                        }
                      }),
            },
          },
        })
          .then(({ data: _data }) => {
            const targetItem = data?.userCart?.items.find(
              item => item.item.id === itemId
            )

            analytics?.track('remove_from_cart', {
              item_list_id: `artist_store_list_${data.userCart?.artist.id}`,
              item_list_name: 'Artist Store Items',
              isAnonymous: user?.isAnonymous,
              items: [
                {
                  item_id: itemId,
                  item_name: targetItem?.item.title,
                  item_category: 'store_item',
                  item_category2: targetItem?.item?.type,
                  item_category3: App.getPlatform(),
                  item_category4: skuId,
                  item_category5: data?.userCart?.artist.id,
                  price: (targetItem?.item?.price ?? 0) * count,
                  quantity: count,
                },
              ],
            })

            if (data?.userCart?.totalCount === count)
              router.replace(routerPages.StoreCartList)
          })
          .catch(showError)
      },
    },
  }
})

const QUERY = gql`
  query UserCart($id: ID!) {
    userCart(id: $id) {
      ...StoreCardDetail
    }
    user {
      id
      fullName
      phoneNumber
      formattedPhoneNumber @client
      isPhoneNumberVerified
      ...UserAddress
    }
  }
  ${StoreCardDetailFragmentDoc}
  ${UserAddressFragmentDoc}
`

const VALIDATE_CART = gql`
  query ValidateCart($inputs: ValidateCartInput!) {
    validateCart(inputs: $inputs) {
      id
    }
  }
`

const ADD_TO_CART = gql`
  mutation AddToCart($inputs: AddToCartInput!) {
    addToCart(inputs: $inputs) {
      id
      totalCount
      totalPrice @client
      formattedTotalPrice @client
      items {
        count
        totalPrice @client
        formattedTotalPrice @client
        item {
          id
          price
          hideSalesNumber
        }
        skus {
          skuId
          count
          sku {
            id
            price
            formattedPrice @client
            unitName
            thumbnail {
              uri
            }
          }
        }
      }
    }
  }
`
const REMOVE_FROM_CART = gql`
  mutation RemoveFromCart($inputs: RemoveFromCartInput!) {
    removeFromCart(inputs: $inputs) {
      id
      totalCount
      totalPrice @client
      formattedTotalPrice @client
      items {
        count
        totalPrice @client
        formattedTotalPrice @client
        item {
          id
          price
          hideSalesNumber
        }
        skus {
          skuId
          count
          sku {
            id
            price
            formattedPrice @client
            unitName
            thumbnail {
              uri
            }
          }
        }
      }
    }
  }
`
